import {BreakpointObserver, BreakpointState} from '@angular/cdk/layout';
import {DOCUMENT, EventEmitter, inject, Injectable, OnDestroy} from '@angular/core';
import {BrowserStore} from '../../stores/browser.store';
import {debounceTime} from 'rxjs/operators';
import {SystemInfoService} from '../system-info/system-info.service';
import {STATIC_CONFIGS} from '../../../configs/static.config';
import {DeviceOrientationEnum} from '../../enums/device-orientation.enum';

@Injectable({
    providedIn: 'root'
})
export class BrowserService implements OnDestroy {
    private readonly smallViewportBreakpoints: Array<string>;
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly browserStore = inject(BrowserStore);
    private readonly window = inject(DOCUMENT).defaultView ?? window;
    private readonly document = inject<Document>(DOCUMENT);
    private readonly systemInfoService = inject(SystemInfoService);
    private readonly orientationChangeEventEmitter: EventEmitter<DeviceOrientationEnum> = new EventEmitter();
    private readonly matchOrientationMediaQueryList: MediaQueryList;
    private readonly emitOrientationChangeEventFunction: (mediaQueryListEvent: MediaQueryListEvent) => void;

    public constructor() {
        this.smallViewportBreakpoints = [
            `(max-width: ${STATIC_CONFIGS.ui.breakpoints.twoColumnMinWidth - 1}px)`,
            `(max-height: ${STATIC_CONFIGS.ui.breakpoints.twoColumnMinHeight - 1}px) and (orientation: landscape)`,
        ];
        this.matchOrientationMediaQueryList = this.window.matchMedia('(orientation: landscape)');
        this.emitOrientationChangeEventFunction = (mediaQueryListEvent: MediaQueryListEvent): void => {
            const currentOrientation = mediaQueryListEvent.matches ? DeviceOrientationEnum.LANDSCAPE : DeviceOrientationEnum.PORTRAIT;

            this.orientationChangeEventEmitter.emit(currentOrientation);
        };
    }

    public ngOnDestroy(): void {
        this.matchOrientationMediaQueryList.removeEventListener('change', this.emitOrientationChangeEventFunction);
    }

    public setup(): void {
        this.browserStore.update({
            orientation: this.getCurrentOrientation(),
            hasSmallViewport: this.breakpointObserver.isMatched(this.smallViewportBreakpoints),
            isOnline: this.window.navigator.onLine,
        });

        this.matchOrientationMediaQueryList
            .addEventListener('change', this.emitOrientationChangeEventFunction);

        // @TODO: Replace breakpointObserver (CDK)
        this.breakpointObserver.observe(this.smallViewportBreakpoints)
            .pipe(debounceTime(100))
            .subscribe((result: BreakpointState) => {
                this.setSmallViewport(result.matches);
            });

        this.window.ononline = (): void => this.updateIsOnline();
        this.window.onoffline = (): void => this.updateIsOnline();

        if (this.systemInfoService.isCapacitor()) {
            const capacitorKeyboardPlugin = import('@capacitor/keyboard');

            void capacitorKeyboardPlugin.then(async plugin => {
                const virtualKeyboardIsOpenCssClass = 'virtual-keyboard-is-open';

                await plugin.Keyboard.addListener('keyboardWillShow', () => {
                    this.document.body.classList.add(virtualKeyboardIsOpenCssClass);
                });
                await plugin.Keyboard.addListener('keyboardWillHide', () => {
                    this.document.body.classList.remove(virtualKeyboardIsOpenCssClass);
                });
            });
        }
    }

    public getOrientationChangeEventEmitter(): EventEmitter<DeviceOrientationEnum> {
        return this.orientationChangeEventEmitter;
    }

    private getCurrentOrientation(): DeviceOrientationEnum | undefined {
        return this.window.screen.orientation.type.includes(DeviceOrientationEnum.LANDSCAPE.toLowerCase()) ? DeviceOrientationEnum.LANDSCAPE : DeviceOrientationEnum.PORTRAIT;
    }

    private setSmallViewport(hasSmallViewport: boolean): void {
        this.browserStore.update({
            orientation: this.getCurrentOrientation(),
            hasSmallViewport
        });
    }

    private updateIsOnline(): void {
        this.browserStore.update({ isOnline: this.window.navigator.onLine });
    }
}
